/**
 * Stan „w trakcie edycji” widoczności i kolejności kolumn (zakładka Widoczność kolumn)
 * oraz akcje: toggle, zastosuj do Redux, wykrywanie niezapisanych zmian.
 */
import { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'store';
import { reduxActions } from 'store';
import type { IWiseTableColumn } from 'components/wise/types';

export interface UseColumnVisibilityAndOrderParams {
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
  columns: IWiseTableColumn[];
  isDynamicUI: boolean;
  pageSymbol?: string;
  componentSymbol: string;
}

export function useColumnVisibilityAndOrder({
  columnVisibility,
  columnOrder,
  columns,
  isDynamicUI,
  pageSymbol,
  componentSymbol
}: UseColumnVisibilityAndOrderParams) {
  const dispatch = useDispatch();
  const [pendingColumnVisibility, setPendingColumnVisibility] = useState<Record<string, boolean>>({});
  const [pendingColumnOrder, setPendingColumnOrderState] = useState<string[]>([]);

  const onColumnVisibilityChange = useCallback(
    (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => {
      const next = updater(columnVisibility);
      if (isDynamicUI && pageSymbol) {
        dispatch(
          reduxActions.setWiseTableColumnVisibility({
            pageSymbol,
            componentSymbol,
            columnVisibility: next
          })
        );
      } else {
        dispatch(
          reduxActions.setTraderPanelWiseTableColumnVisibility({
            componentSymbol,
            columnVisibility: next
          })
        );
      }
    },
    [columnVisibility, isDynamicUI, pageSymbol, componentSymbol, dispatch]
  );

  const handleColumnVisibilityToggle = useCallback(
    (fieldSymbol: string, visible: boolean) => {
      setPendingColumnVisibility((prev) => ({
        ...prev,
        [fieldSymbol]: visible
      }));
    },
    []
  );

  const applyColumnVisibility = useCallback(() => {
    if (isDynamicUI && pageSymbol) {
      dispatch(
        reduxActions.setWiseTableColumnVisibility({
          pageSymbol,
          componentSymbol,
          columnVisibility: pendingColumnVisibility
        })
      );
      dispatch(
        reduxActions.setWiseTableColumnOrder({
          pageSymbol,
          componentSymbol,
          columnOrder: pendingColumnOrder
        })
      );
    } else {
      dispatch(
        reduxActions.setTraderPanelWiseTableColumnVisibility({
          componentSymbol,
          columnVisibility: pendingColumnVisibility
        })
      );
      dispatch(
        reduxActions.setTraderPanelWiseTableColumnOrder({
          componentSymbol,
          columnOrder: pendingColumnOrder
        })
      );
    }
  }, [pendingColumnVisibility, pendingColumnOrder, isDynamicUI, pageSymbol, componentSymbol, dispatch]);

  const hasPendingColumnOrderChange = useMemo(() => {
    const applied = columnOrder.length > 0 ? columnOrder : columns.map((c) => c.field_symbol);
    const pending = pendingColumnOrder.length > 0 ? pendingColumnOrder : columns.map((c) => c.field_symbol);
    if (applied.length !== pending.length) return true;
    return applied.some((id, i) => id !== pending[i]);
  }, [columnOrder, pendingColumnOrder, columns]);

  const hasPendingColumnVisibilityChange = useMemo(() => {
    const keys = new Set([
      ...Object.keys(columnVisibility),
      ...Object.keys(pendingColumnVisibility)
    ]);
    const visibilityChanged = Array.from(keys).some(
      (k) => (columnVisibility[k] !== false) !== (pendingColumnVisibility[k] !== false)
    );
    return visibilityChanged || hasPendingColumnOrderChange;
  }, [columnVisibility, pendingColumnVisibility, hasPendingColumnOrderChange]);

  const setPendingColumnOrder = useCallback(
    (orderOrUpdater: string[] | ((prev: string[]) => string[])) => {
      setPendingColumnOrderState(
        typeof orderOrUpdater === 'function' ? orderOrUpdater : () => orderOrUpdater
      );
    },
    []
  );

  return {
    pendingColumnVisibility,
    setPendingColumnVisibility,
    pendingColumnOrder,
    setPendingColumnOrderState,
    setPendingColumnOrder,
    onColumnVisibilityChange,
    handleColumnVisibilityToggle,
    applyColumnVisibility,
    hasPendingColumnVisibilityChange
  };
}
